import React, { useState, useCallback, useRef } from 'react';
import { Select } from 'syngenta-digital-cropwise-react-ui-kit';
import { Button } from '@agconnections/grow-ui';
import ItemsDropdown from 'components/ItemsDropdown';
import EquipmentIcon from 'assets/equipment.svg?react';
import PlusIcon from 'assets/plus.svg?react';
import focusInput from 'screens/Applications&WorkOrders/StepsLayout/Helpers/focusInput';
import filterOptionByNormalizedString from 'screens/Applications&WorkOrders/StepsLayout/Helpers/filterOptions';
import getDropdownParentWithoutDetaching from 'utilities/getDropdownParentWithoutDetaching';
import AddNewEquipmentDrawer from '../AddNewEquipmentDrawer';
import { EquipmentItem, EquipmentDropdownBaseProps } from '../../types';
import EquipmentTag, { EquipmentTagProps } from '../EquipmentTag';
import EquipmentDropdownItem from '../EquipmentDropdownItem';

/**
 * Creates a value renderer function for the dropdown
 * @param value - The selected value
 * @param allowSearch - Whether search is allowed
 * @returns The value renderer function or null
 */
const createValueRenderer = (
  value: EquipmentItem[] | null,
  allowSearch?: boolean,
  mode?: 'multiple' | 'single'
): React.ReactNode | string | null => {
  if (!value || value.length === 0) {
    return null;
  }

  // For single selection mode
  if (mode === 'single') {
    if (allowSearch) {
      return value[0].name;
    }

    return (
      <div
        className="w-full h-full p-2 flex items-center"
        data-testid="selected-equipment"
      >
        <span>
          <EquipmentIcon />
        </span>
        <div className="pl-2">{value[0].name}</div>
      </div>
    );
  }

  // For multiple selection mode, we don't render anything here
  // as the tags will be rendered by the Select component
  return null;
};

/**
 * Base component for EquipmentDropdown
 * Handles rendering the dropdown and drawer
 */
const EquipmentDropdownBase: React.FC<EquipmentDropdownBaseProps> = ({
  bottomButtonText,
  bottomButtonIcon = <PlusIcon />,
  onChange,
  value,
  id,
  title,
  allowSearch,
  items,
  hideIcon,
  loading,
  refetchEquipment,
  mode = 'single',
  placeholder
}) => {
  const [addNewEquipmentDrawerOpen, setAddNewEquipmentDrawerOpen] = useState(
    false
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const refocusParentInput = useCallback(() => {
    focusInput(containerRef, 'input');
  }, []);

  const valueRenderer = useCallback(
    () => createValueRenderer(value, allowSearch, mode),
    [value, allowSearch, mode]
  );

  // Create a custom ItemComponent that includes the hideIcon prop
  const ItemComponent = useCallback(
    (props: { item: any }) => (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <EquipmentDropdownItem {...props} hideIcon={hideIcon} />
    ),
    [hideIcon]
  );

  // Handle equipment addition
  const handleEquipmentAdded = useCallback(
    (newEquipment: EquipmentItem) => {
      // Only add the new equipment if it was successfully created
      if (newEquipment) {
        setAddNewEquipmentDrawerOpen(false);

        // Make sure we have a complete equipment object with both id and name
        if (
          typeof newEquipment === 'object' &&
          newEquipment.id &&
          newEquipment.name
        ) {
          const newValue = value ? [...value, newEquipment] : [newEquipment];
          onChange(newValue);

          if (refetchEquipment) {
            refetchEquipment();
          }

          refocusParentInput();
        }
      }
    },
    [value, onChange, refetchEquipment, refocusParentInput]
  );

  const handleDrawerClose = useCallback(() => {
    setAddNewEquipmentDrawerOpen(false);
    refocusParentInput();
  }, [refocusParentInput]);

  const dropdownFilterHandler = (
    searchText: string,
    item: {
      value: {
        name: string;
      };
    }
  ) => {
    if (!searchText) return true;
    const lowerCaseSearchText = searchText.toLowerCase();
    return item.value?.name?.toLowerCase().includes(lowerCaseSearchText);
  };

  // For multi-selection mode, we need to handle the equipment dropdown differently
  if (mode === 'multiple') {
    // Convert items to options for Select component
    const options =
      items?.flatMap(group =>
        group.items.map(item => ({
          value: item.value.id,
          label: item.value.name,
          data: item.value,
          'data-testid': `${item.value.name}-option`
        }))
      ) || [];

    // Create a map of equipment IDs to names for tag rendering
    const equipmentMap = new Map<string, string>();
    options.forEach(option => {
      equipmentMap.set(option.value, option.label);
    });

    // Also add any selected items that might not be in options yet
    if (value) {
      value.forEach(item => {
        if (item.id && item.name) {
          equipmentMap.set(item.id, item.name);
        }
      });
    }

    // Handle change for Select component
    const handleSelectChange = (selectedValues: string[]) => {
      if (!selectedValues || selectedValues.length === 0) {
        onChange([]);
        return;
      }

      // Map selected values back to equipment items
      const selectedItems = selectedValues
        .map(selectedValue => {
          const option = options.find(opt => opt.value === selectedValue);
          return option?.data;
        })
        .filter(Boolean) as EquipmentItem[];

      onChange(selectedItems);
    };

    const tagRender = (props: EquipmentTagProps & { value: string }) => {
      const equipmentName = equipmentMap.get(props.value);
      return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <EquipmentTag {...props} label={equipmentName || props.label} />
      );
    };

    const dropdownRender = (menu: React.ReactElement) => (
      <div>
        {menu}
        {bottomButtonText && (
          <div className="flex justify-center border-t border-solid border-neutral-100">
            <div className="mx-4 my-2 w-full h-full">
              <Button
                onClick={() => setAddNewEquipmentDrawerOpen(true)}
                style={{ width: '100%' }}
                icon={bottomButtonIcon}
                type="outline"
                ghost
              >
                {bottomButtonText}
              </Button>
            </div>
          </div>
        )}
      </div>
    );

    return (
      <div
        ref={containerRef}
        className="cwf-equipment-dropdown equipment-select"
        style={{ overflow: 'visible' }}
      >
        <Select
          id={id}
          data-testid={id}
          mode="multiple"
          style={{ width: '100%' }}
          placeholder={placeholder}
          className="custom-select"
          loading={loading}
          value={value?.map(item => item.id) || []}
          onChange={handleSelectChange}
          options={options}
          tagRender={tagRender}
          filterOption={filterOptionByNormalizedString}
          showSearch
          dropdownRender={dropdownRender}
          getPopupContainer={getDropdownParentWithoutDetaching}
        />
        {addNewEquipmentDrawerOpen && (
          <AddNewEquipmentDrawer
            onClose={handleDrawerClose}
            onEquipmentAdded={handleEquipmentAdded}
            open={addNewEquipmentDrawerOpen}
            title={title}
          />
        )}
      </div>
    );
  }

  // For single selection mode, use the ItemsDropdown component
  return (
    <div ref={containerRef}>
      <ItemsDropdown
        rightOffset={0}
        heightClass="h-64"
        valueRenderer={valueRenderer}
        value={value?.[0] || null}
        bottomButtonText={bottomButtonText}
        bottomButtonIcon={bottomButtonIcon}
        onBottomButtonClick={() => {
          setAddNewEquipmentDrawerOpen(true);
        }}
        onChange={(selectedItem: EquipmentItem) => {
          onChange(selectedItem ? [selectedItem] : []);
        }}
        id={id}
        items={items}
        ItemComponent={ItemComponent}
        mergeSearchInputWithValue={allowSearch}
        searchPlaceholderText=""
        onFilter={allowSearch ? dropdownFilterHandler : undefined}
        isLoading={loading}
      />
      {addNewEquipmentDrawerOpen && (
        <AddNewEquipmentDrawer
          onClose={handleDrawerClose}
          onEquipmentAdded={handleEquipmentAdded}
          open={addNewEquipmentDrawerOpen}
          title={title}
        />
      )}
    </div>
  );
};

export default EquipmentDropdownBase;
